import { createRootRoute, createRoute, createRouter, Outlet } from '@tanstack/react-router';
import { z } from 'zod';
import { AppShell } from './components/AppShell.js';
import { TodayScreen } from './screens/Today.js';
import { CustomersScreen } from './screens/Customers.js';
import { CustomerDetailScreen } from './screens/CustomerDetail.js';
import { JobsScreen } from './screens/Jobs.js';
import { JobDetailScreen } from './screens/JobDetail.js';
import { InvoicesScreen } from './screens/Invoices.js';
import { QuotesScreen } from './screens/Quotes.js';
import { InboxScreen } from './screens/Inbox.js';
import { InvoiceDetailScreen } from './screens/InvoiceDetail.js';
import { QuoteDetailScreen } from './screens/QuoteDetail.js';
import { UnbilledScreen } from './screens/Unbilled.js';
import { BoardScreen } from './screens/Board.js';
import { TeamScreen } from './screens/Team.js';
import { SignInScreen } from './screens/SignIn.js';
import { CustomerNewScreen } from './screens/CustomerNew.js';
import { CustomerEditScreen } from './screens/CustomerEdit.js';
import { JobNewScreen } from './screens/JobNew.js';
import { JournalScreen } from './screens/Journal.js';
import { IntakeScreen } from './screens/Intake.js';
import { AddressHistoryScreen } from './screens/AddressHistory.js';
import { TranscriptsScreen } from './screens/Transcripts.js';
import { NoticesScreen } from './screens/Notices.js';
import { assertRoutesReserved, readTenantSlug } from './lib/tenant.js';

/**
 * Routing, and the rule that governs it.
 *
 * **Every piece of screen state that matters lives in the URL.** Which tab is open, which
 * filters are applied, how a table is sorted, which record is selected, what is typed in
 * the search box — all of it is a validated search parameter.
 *
 * The requirement was "F5 should keep me where I was", and this is the only way to actually
 * satisfy it. It also makes a screen shareable ("look at this filtered list"), makes the
 * back button behave, and means a crash never loses more than the current keystroke.
 *
 * The discipline this imposes: **no screen may hold state in `useState` that the user would
 * be annoyed to lose on reload.** Transient interaction state — a hover, an open menu, an
 * unsent draft — is fine. A filter is not.
 *
 * Zod schemas here are the contract. An old bookmark with a since-removed filter degrades
 * to the default rather than crashing the screen.
 */

/** Search parameters available on every route, because the search overlay is global. */
const globalSearch = z.object({
  /** The global search query. Present means the overlay is open. */
  search: z.string().optional(),
  /**
   * The record open in the side panel, as `kind:id`.
   *
   * In the URL for the same reason as everything else: a reload, a shared link and the back
   * button must all restore the same view, panel included.
   */
  peek: z.string().max(80).optional(),
});

/**
 * The parameters every list screen carries, whatever it is a list of.
 *
 * Merged into each list route rather than declared five times, because they are one contract:
 * `useListView` reads and writes exactly these, and a route that spelled one of them differently
 * would silently drop it on the next navigation — a filter that vanishes when you change another
 * filter, which is the kind of bug people work around for months instead of reporting.
 *
 * `sort` and `dateField` stay per route: each endpoint has its own closed set of columns, and the
 * whole point of validating them here is that an old bookmark naming a since-removed column
 * degrades to the default rather than reaching the API at all.
 */
const listSearch = z.object({
  /** Free-text filter. */
  q: z.string().optional(),
  /** Reverses whatever `sort` names. Absent means the column's natural direction. */
  dir: z.enum(['asc', 'desc']).optional(),
  /**
   * The period, symbolically. Absent means "the period I have set as my default", which is why
   * it is optional rather than defaulted — a default here would overwrite the preference.
   */
  window: z.enum(['7d', '30d', '90d', '365d', 'all', 'custom']).optional(),
  /** Only meaningful alongside `window: 'custom'`. */
  from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  page: z.coerce.number().int().min(1).optional(),
  pageSize: z.coerce.number().int().min(1).max(200).optional(),
  cursor: z.string().optional(),
});

const rootRoute = createRootRoute({
  validateSearch: globalSearch,
  component: () => (
    <AppShell>
      <Outlet />
    </AppShell>
  ),
});

const todayRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  validateSearch: globalSearch.extend({
    /** Which day the board shows. Defaults to today, but a reload keeps the chosen day. */
    date: z.string().optional(),
    assignee: z.string().optional(),
  }),
  component: TodayScreen,
});

/**
 * The schedule board — who is out today, and where.
 *
 * `date` is a search parameter rather than a path segment because the day is a filter on one
 * screen, not a different screen; that also means the back button steps out of the board
 * instead of walking back through every day you paged through.
 */
const boardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/board',
  validateSearch: globalSearch.extend({
    /** `YYYY-MM-DD`. Absent means today, so the bare URL is always current. */
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    /**
     * Narrows the board to people who actually have work. Off by default: an empty column is
     * the useful signal that somebody is free.
     */
    hideIdle: z.boolean().optional(),
    /** Narrows the grid to one technician's day. Set by clicking their card. */
    only: z.string().optional(),
  }),
  component: BoardScreen,
});

/**
 * The team. Who can sign in, as what, and in which of the businesses.
 */
const teamRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/team',
  validateSearch: globalSearch.extend({
    /** The person whose panel is open. In the URL so it can be linked to. */
    person: z.string().optional(),
    /** People, or the roles themselves. Two questions, two views. */
    view: z.enum(['people', 'roles']).optional(),
    filter: z.enum(['active', 'technicians', 'former', 'all']).default('active'),
    q: z.string().optional(),
  }),
  component: TeamScreen,
});

/**
 * Sign-in. Rendered without the application chrome — see `AppShell`, which steps aside for
 * this path: somebody who is not signed in must not be shown navigation for a workspace they
 * cannot reach. `email` and `code` come from the URL, because the operator command prints a
 * link carrying both.
 */
const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/connexion',
  component: SignInScreen,
});

/**
 * The audit trail. Filters and page live in the URL like everything else, so a colleague can
 * be sent "what happened to invoices on Tuesday" as a link.
 */
const journalRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/journal',
  validateSearch: globalSearch.extend(listSearch.shape).extend({
    actor: z.string().optional(),
    entity: z.string().optional(),
    /** A single day, kept alongside the period because "what happened Tuesday" is one click. */
    on: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    sort: z.enum(['at', 'entity', 'action', 'actor']).default('at'),
  }),
  component: JournalScreen,
});

const intakeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/intake',
  validateSearch: globalSearch.extend(listSearch.shape).extend({
    state: z.enum(['open', 'handled', 'all']).optional(),
    kind: z.enum(['voicemail', 'missed', 'unmatched', 'all']).optional(),
    reason: z.string().optional(),
    /** Which call is open in the right-hand pane, so it can be linked to. */
    call: z.string().uuid().optional(),
  }),
  component: IntakeScreen,
});

const addressRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/addresses/$addressId',
  validateSearch: globalSearch.extend({
    /** The home, or the building it sits in. Absent means the home. */
    scope: z.enum(['place', 'building']).optional(),
  }),
  component: AddressHistoryScreen,
});

const transcriptsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/transcripts',
  validateSearch: globalSearch.extend({
    state: z.enum(['pending', 'reviewed', 'all']).optional(),
    disagreeing: z.boolean().optional(),
  }),
  component: TranscriptsScreen,
});

/**
 * Notices and terms.
 *
 * `notice` in the search parameter rather than the path, for the same reason every other
 * selection is: a reload, a shared link and the back button all have to restore the same view.
 * `new` is a reserved value meaning the editor with nothing in it.
 */
const noticesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/notices',
  validateSearch: globalSearch.extend({ notice: z.string().max(60).optional() }),
  component: NoticesScreen,
});

const customersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/customers',
  /**
   * A directory is browsed by jumping around, so this list pages by number rather than by
   * cursor. Both are in the URL, so a page and a page size survive a reload and a share.
   */
  validateSearch: globalSearch.extend(listSearch.shape).extend({
    tag: z.string().optional(),
    city: z.string().optional(),
    sort: z.enum(['name', 'recent', 'balance']).default('recent'),
    dateField: z.enum(['activity', 'created']).optional(),
  }),
  component: CustomersScreen,
});

/**
 * Creating a customer.
 *
 * A route rather than a dialog: the back button, a reload and a shared link all then behave,
 * and this screen is reached from the list, from the empty state and from search. `new` is a
 * static segment, which TanStack ranks above `$customerId`, so it cannot be read as an id.
 */
const customerNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/customers/new',
  component: CustomerNewScreen,
});

const customerDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/customers/$customerId',
  validateSearch: globalSearch.extend({
    /**
     * The customer page shows the whole relationship on one scrollable page; the tab only
     * chooses which history is expanded. It is in the URL so a colleague can be sent
     * straight to the call log.
     */
    tab: z.enum(['overview', 'jobs', 'quotes', 'invoices', 'calls', 'messages', 'history']).default('overview'),
    call: z.string().optional(),
  }),
  component: CustomerDetailScreen,
});

/**
 * Correcting a customer. A route of its own rather than an editing mode on the file: the file
 * is a dense summary that shows only the primary phone, email and address, and editing needs
 * every row.
 */
const customerEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/customers/$customerId/edit',
  component: CustomerEditScreen,
});

const jobsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/jobs',
  validateSearch: globalSearch.extend(listSearch.shape).extend({
    /** Board for dispatching, list for scanning. Both are legitimate; remember the choice. */
    view: z.enum(['board', 'list']).default('board'),
    stage: z.string().optional(),
    assignee: z.string().optional(),
    service: z.string().optional(),
    unassignedOnly: z.boolean().default(false),
    sort: z.enum(['scheduled', 'created', 'balance', 'number', 'customer', 'activity']).default('scheduled'),
    dateField: z.enum(['created', 'scheduled', 'activity']).optional(),
  }),
  component: JobsScreen,
});

/**
 * Raising a job. `customerId` is optional and carries the choice already made by whoever sent
 * the user here — from a customer's file it is known, from the jobs list it is not.
 */
const jobNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/jobs/new',
  validateSearch: globalSearch.extend({ customerId: z.string().uuid().optional() }),
  component: JobNewScreen,
});

const jobDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/jobs/$jobId',
  validateSearch: globalSearch.extend({
    tab: z.enum(['work', 'money', 'messages', 'time', 'photos', 'history']).default('work'),
  }),
  component: JobDetailScreen,
});

const quotesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/quotes',
  validateSearch: globalSearch.extend(listSearch.shape).extend({
    status: z.enum(['all', 'draft', 'pending', 'accepted', 'declined', 'expired']).default('pending'),
    sort: z.enum(['issued', 'expires', 'total', 'number', 'customer']).default('issued'),
    dateField: z.enum(['issued', 'expires', 'created']).optional(),
  }),
  component: QuotesScreen,
});

const invoicesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/invoices',
  validateSearch: globalSearch.extend(listSearch.shape).extend({
    // Defaults to what is owed, because that is why anyone opens this screen.
    status: z.enum(['all', 'draft', 'awaiting_payment', 'partial', 'paid', 'voided']).default('awaiting_payment'),
    overdueOnly: z.boolean().default(false),
    /*
     * Newest first. It was the due date ascending, which is right for chasing money and wrong
     * for everything else: choosing "Payée" opened on the oldest settled invoice in the archive.
     */
    sort: z.enum(['due', 'issued', 'balance', 'total', 'number', 'customer', 'created']).default('issued'),
    dateField: z.enum(['issued', 'due', 'paid', 'created']).optional(),
  }),
  component: InvoicesScreen,
});

/**
 * Work done and never billed.
 *
 * Its own screen rather than a filter on the invoice list, because it is a different job:
 * the invoice list answers "who owes us money", this answers "what have we forgotten to
 * charge for". In the imported history that is $36,694 across 109 drafts, a quarter of it on
 * jobs already finished.
 */
const unbilledRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/unbilled',
  validateSearch: globalSearch.extend({
    /** `ready` narrows to drafts whose job is already closed — nothing blocks sending those. */
    scope: z.enum(['all', 'ready']).default('all'),
    sort: z.enum(['age', 'value', 'customer']).default('age'),
    dir: z.enum(['asc', 'desc']).optional(),
  }),
  component: UnbilledScreen,
});

const invoiceDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/invoices/$invoiceId',
  validateSearch: globalSearch,
  component: InvoiceDetailScreen,
});

const quoteDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/quotes/$quoteId',
  validateSearch: globalSearch,
  component: QuoteDetailScreen,
});

const inboxRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/inbox',
  validateSearch: globalSearch.extend({
    /** The open conversation. In the URL so a thread can be linked to directly. */
    thread: z.string().optional(),
    filter: z.enum(['all', 'unread', 'customer', 'internal', 'mine']).default('all'),
    /** Search across customer names and message text. */
    q: z.string().optional(),
    message: z.string().optional(),
  }),
  component: InboxScreen,
});

const routeTree = rootRoute.addChildren([
  todayRoute,
  signInRoute,
  boardRoute,
  teamRoute,
  journalRoute,
  intakeRoute,
  addressRoute,
  transcriptsRoute,
  noticesRoute,
  customersRoute,
  customerNewRoute,
  customerEditRoute,
  customerDetailRoute,
  jobsRoute,
  jobNewRoute,
  jobDetailRoute,
  quotesRoute,
  quoteDetailRoute,
  invoicesRoute,
  invoiceDetailRoute,
  unbilledRoute,
  inboxRoute,
]);

/*
 * Every route's first segment must be reserved, or `readTenantSlug` reads it as a business
 * name and the whole screen silently talks to `/api/<route>/…`. Checked here because this is
 * the one file that knows all the paths.
 */
assertRoutesReserved(routeTree.children?.map((route: { path?: string }) => route.path ?? '') ?? []);

export const router = createRouter({
  routeTree,
  /*
   * The business slug is the router's basepath, not a segment in every route.
   *
   * That one line is why `<Link to="/customers">` still works untouched across every screen:
   * the router resolves it to `/hydrokleen/customers` on its own. Threading a `$tenantSlug`
   * param through each route would have meant editing every link and every navigate call in
   * the application to achieve exactly the same URLs.
   */
  basepath: readTenantSlug() ?? undefined,
  // Filters and tabs should not each create a history entry: pressing back after adjusting
  // three filters must return to the previous screen, not step back through the filters.
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
